import * as React from "react";
import { styled } from "@mui/material";
import { QualifiedFeature, QualifiedFeatureFragment } from "fe-common/graphql";
import { useMemo } from "react";
import { KahunCategories } from "shared";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { getQFAndRefinementsLines } from "fe-common/logic";
import { findingFormat } from "../formatting/FindingNameFormatter";

//region [[ Styles ]]
const Title = styled("div")({
  marginBottom: "26px",
  fontSize: "16px",
  fontWeight: "bold",
});

const Name = styled("div")({
  margin: "0 0 0 46px",
  color: KahunColorMap.PrimaryDim,
});

const Refinements = styled("div")({
  margin: "8px 0 0 68px",
  maxWidth: "400px",
});

const Divider = styled("div")({
  margin: "16px 48px",
  border: "1px solid #E9E9E9",
});
//endregion [[ Styles ]]

//region [[ Props ]]

interface CommonPatientCardChartProps {
  id?: string;
  patientCase: {
    main: QualifiedFeature[];
    clinicalFeatures: QualifiedFeature[];
    reviewOfSystems: QualifiedFeature[];
    background: QualifiedFeature[];
  };
}

//endregion [[ Props ]]

//region [[ Functions ]]
interface FindingItemProps {
  qf: QualifiedFeatureFragment;
  isLast: boolean;
}
const FindingItem = ({ qf, isLast }: FindingItemProps) => {
  const [findingTitle, findingQualifiersText] = getQFAndRefinementsLines(qf);
  return (
    <React.Fragment key={findingTitle}>
      <Divider />
      <Name>{findingFormat(qf)}</Name>
      <Refinements>{findingQualifiersText}</Refinements>
      <Divider />
    </React.Fragment>
  );
};
const findings = ({ qfs }: { qfs: QualifiedFeatureFragment[] }) =>
  qfs.map((qf, index, arr) => (
    <FindingItem key={qf.id} qf={qf} isLast={index === arr.length - 1} />
  ));
//endregion [[ Functions ]]

export const CommonPatientCardChart = ({
  patientCase,
  ...props
}: CommonPatientCardChartProps) => {
  const clinicalFindings: QualifiedFeatureFragment[] = useMemo(
    () => [
      ...patientCase.main,
      ...patientCase.clinicalFeatures,
      ...patientCase.reviewOfSystems,
    ],
    [patientCase],
  );

  const symptoms = useMemo(
    () =>
      clinicalFindings.filter(
        (qf) => qf.feature.category.id === KahunCategories.KAHUN_SYMPTOMS,
      ),
    [clinicalFindings],
  );

  const presentSymptoms = useMemo(() => symptoms.filter((qf) => !qf.negate), [
    symptoms,
  ]);

  const absentSymptoms = useMemo(() => symptoms.filter((qf) => qf.negate), [
    symptoms,
  ]);

  const signs = useMemo(
    () =>
      clinicalFindings
        .filter((qf) => qf.feature.category.id === KahunCategories.SIGN)
        .filter((qf) => !qf.negate),
    [clinicalFindings],
  );

  const vitalSigns = useMemo(
    () =>
      clinicalFindings.filter(
        (qf) => qf.feature.category.id === KahunCategories.VITAL_SIGNS,
      ),
    [clinicalFindings],
  );

  return (
    <div id={props.id}>
      {symptoms.length > 0 && <Title>Clinical Findings</Title>}
      {presentSymptoms.length > 0 && (
        <>
          <div>Present symptoms</div>
          {findings({ qfs: presentSymptoms })}
        </>
      )}
      {absentSymptoms.length > 0 && (
        <>
          <div>Absent symptoms</div>
          {findings({ qfs: absentSymptoms })}
        </>
      )}
      {patientCase.background.length > 0 && (
        <>
          <Title>Background</Title>
          <div>Medical history </div>
          {findings({ qfs: patientCase.background })}
        </>
      )}
      {(signs.length > 0 || vitalSigns.length > 0) && (
        <Title>Physical examination</Title>
      )}
      {signs.length > 0 && (
        <>
          <div>Present signs</div>
          {findings({ qfs: signs })}
        </>
      )}
      {vitalSigns.length > 0 && (
        <>
          <div>Vital Signs</div>
          {findings({ qfs: vitalSigns })}
        </>
      )}
    </div>
  );
};
